import {
  runTurnSseEventSchema,
  type StreamTurnSseEvent,
  streamTurnSseEventSchema,
  turnActionPath,
} from '@salescore/agent-contract'
import { Chunk, Effect, Option, Schedule, Stream } from 'effect'

import { type ChatEvent, chatEventsOfRecord, chatEventsOfRunEvent } from '../domain/chatEvents'
import { type AgentApi, AgentApiError } from './agentApi'
import { mergeAnswerDeltas, RUNTIME_EVENT_BATCH_WINDOW } from './runtimeEventBatcher'
import { parseSseData, readSseEvents } from './sse'

// 指示の応答（runtime の run の SSE）と、途中からの配信（storage の streamTurn の SSE）の読み取り

// 1回にまとめる件数の上限。件数に達するか32msがたつまでの出来事を1回で渡す。
const MAX_EVENTS_PER_BATCH = 1000

// run の配信が切れた後に、storage の配信でつなぎ直す回数と間隔（playground と同じ。最初の1回と、間隔をあけた4回）
const STORAGE_RESUME_SCHEDULE = Schedule.intersect(Schedule.spaced('1 second'), Schedule.recurs(4))

const isTurnFinished = (events: readonly ChatEvent[]): boolean => events.some(({ kind }) => kind === 'turnFinished')

/**
 * run の SSE を読み、32ms ごとにまとめた出来事を onEvents に渡す。ターンの終わり（completed か error）を受けたかを返す。
 * 形の合わないイベントは表示に使わずに捨てる。配信が途中で切れたら、受け取った分までで終える（呼び出し側が storage でつなぎ直す）
 */
export const receiveRunTurnEvents = (
  response: Response,
  onEvents: (events: readonly ChatEvent[]) => void,
): Effect.Effect<boolean> =>
  readSseEvents(response).pipe(
    Stream.catchAll(() => Stream.empty),
    Stream.filterMap(({ event, data }) => {
      const parsed = runTurnSseEventSchema.safeParse({ event, data: parseSseData(data) })
      return parsed.success ? Option.some(chatEventsOfRunEvent(parsed.data)) : Option.none()
    }),
    Stream.flattenIterables,
    Stream.groupedWithin(MAX_EVENTS_PER_BATCH, RUNTIME_EVENT_BATCH_WINDOW),
    Stream.runFoldEffect(false, (isEnded, chunk) =>
      Effect.sync(() => {
        const events = mergeAnswerDeltas(Chunk.toReadonlyArray(chunk))
        onEvents(events)
        return isEnded || isTurnFinished(events)
      }),
    ),
  )

interface StorageStreamHandlers {
  // 最初の記録が届いたときに、そのターンの表示を消す。記録の最初から作り直すので、つなぎ直しを試し直しても二重にならない
  reset: () => void
  receive: (events: readonly ChatEvent[]) => void
  // storage が配信を始めた後に失敗した（error イベント）。message は storage の文言
  fail: (message: string) => void
}

interface StorageStreamProgress {
  needsReset: boolean
  // reconnect（配信の上限）で終わったときの、続きを読む位置
  reconnectOffset: number | null
}

const streamFromOffset = (
  api: AgentApi,
  target: { agentSessionId: string; turnId: string },
  start: { offset: number; needsReset: boolean },
  handlers: StorageStreamHandlers,
): Effect.Effect<void, AgentApiError> =>
  Effect.gen(function* () {
    const query = new URLSearchParams({ offset: String(start.offset) }).toString()
    const response = yield* api(`${turnActionPath(target, 'stream')}?${query}`, { method: 'GET' })
    const progress = yield* readSseEvents(response).pipe(
      // 形の合わないイベントは、配信の失敗として試し直す
      Stream.mapEffect(({ event, data }) => {
        const parsed = streamTurnSseEventSchema.safeParse({ event, data: parseSseData(data) })
        return parsed.success
          ? Effect.succeed(parsed.data)
          : Effect.fail(
              new AgentApiError({
                reason: 'invalidResponse',
                status: response.status,
                body: null,
                cause: parsed.error.issues,
              }),
            )
      }),
      Stream.runFoldEffect(
        { needsReset: start.needsReset, reconnectOffset: null },
        (current: StorageStreamProgress, streamEvent: StreamTurnSseEvent): Effect.Effect<StorageStreamProgress> =>
          Effect.sync(() => {
            switch (streamEvent.event) {
              case 'records': {
                if (current.needsReset) {
                  handlers.reset()
                }
                handlers.receive(streamEvent.data.records.flatMap(chatEventsOfRecord))
                return { ...current, needsReset: false }
              }
              // ターンの終わりは、records の turn.completed で表示に映す
              case 'completed': {
                return current
              }
              case 'reconnect': {
                return { ...current, reconnectOffset: streamEvent.data.nextOffset }
              }
              case 'error': {
                handlers.fail(streamEvent.data.error.message)
                return current
              }
            }
          }),
      ),
    )
    if (progress.reconnectOffset !== null) {
      yield* streamFromOffset(
        api,
        target,
        { offset: progress.reconnectOffset, needsReset: progress.needsReset },
        handlers,
      )
    }
  })

/**
 * runtime は切断後も実行を続けるため、指示を再送せず storage から配信を再開する。
 * 再接続にも失敗した場合は、呼び出し側が切断を表示する。
 */
export const resumeTurnFromStorage = (
  api: AgentApi,
  target: { agentSessionId: string; turnId: string },
  handlers: StorageStreamHandlers,
): Effect.Effect<void> =>
  streamFromOffset(api, target, { offset: 0, needsReset: true }, handlers).pipe(
    Effect.retry(STORAGE_RESUME_SCHEDULE),
    Effect.catchAll(() => Effect.void),
  )
